// @ts-check
/**
 * Every shipment across every order, in one place — the per-order Shipments panel
 * (inside OrderDetailModal) only shows one order's shipments at a time, so there was
 * previously no way to see or track shipments as a whole, gift orders included.
 */
import { ExternalLink, Eye, RefreshCw, Search } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import OrderDetailModal from '../components/OrderDetailModal';
import BulkActionBar from '../components/ui/BulkActionBar';
import BulkConfirmModal from '../components/ui/BulkConfirmModal';
import { HeaderCheckbox, RowCheckbox } from '../components/ui/RowCheckbox';
import { useToast } from '../context/ToastContext';
import useRowSelection from '../hooks/useRowSelection';
import adminShippingService from '../services/adminShippingService';
import { reportBulkResult, runBulk } from '../services/bulkService';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'created', label: 'Created' },
  { value: 'pickup_scheduled', label: 'Pickup Scheduled' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'out_for_delivery', label: 'Out for Delivery' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'rto', label: 'Returned (RTO)' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'exception', label: 'Exception' },
];

const statusColor = (status) =>
  ({
    created: 'bg-muted text-foreground',
    pickup_scheduled: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
    in_transit: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-300',
    out_for_delivery: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
    delivered: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
    rto: 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300',
    cancelled: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
    exception: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
  })[status] || 'bg-muted text-foreground';

// Statuses the bulk endpoint accepts, and how many labels it will buy in one request.
const BULK_STATUS_OPTIONS = STATUS_OPTIONS.filter((s) =>
  ['pickup_scheduled', 'in_transit', 'out_for_delivery', 'delivered'].includes(s.value),
);
const MAX_LABELS = 25;

const ORDER_TYPE_OPTIONS = [
  { value: '', label: 'All order types' },
  { value: 'standard', label: 'Standard' },
  { value: 'gift', label: 'Gift' },
  { value: 'sample', label: 'Sample' },
];

const ShipmentsPage = () => {
  const toast = useToast();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [orderTypeFilter, setOrderTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalShipments, setTotalShipments] = useState(0);
  const [totals, setTotals] = useState({ shipping_charge: 0, declared_value: 0 });
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [bulkDialog, setBulkDialog] = useState(null); // 'status' | 'label' | 'email'
  const [bulkStatus, setBulkStatus] = useState('in_transit');
  const [bulkBusy, setBulkBusy] = useState(false);
  const pageLimit = 50;

  const loadShipments = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminShippingService.listAllShipments({
        page: currentPage,
        limit: pageLimit,
        search: search || undefined,
        status: statusFilter || undefined,
        order_type: orderTypeFilter || undefined,
      });
      setShipments(data.items || []);
      setTotalPages(data.pages || 1);
      setTotalShipments(data.total || 0);
      setTotals(data.totals || { shipping_charge: 0, declared_value: 0 });
    } catch (error) {
      console.error('Failed to load shipments:', error);
      toast.error('Failed to load shipments');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, orderTypeFilter, currentPage, toast]);

  useEffect(() => {
    loadShipments();
  }, [loadShipments]);

  const handleSearchChange = (value) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleOrderTypeChange = (value) => {
    setOrderTypeFilter(value);
    setCurrentPage(1);
  };

  const formatMoney = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;

  const formatDate = (value) =>
    value
      ? new Date(value).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

  const selection = useRowSelection(
    shipments.map((sh) => sh.id),
    { resetKey: `${search}|${statusFilter}|${orderTypeFilter}|${currentPage}` },
  );

  const runBulkAction = async (action, params, verb) => {
    setBulkBusy(true);
    try {
      const result = await runBulk('/api/admin/shipments/bulk', { ids: selection.selectedIds, action, params });
      reportBulkResult(toast, result, verb, 'shipment');
      setBulkDialog(null);
      selection.clear();
      loadShipments();
    } catch (error) {
      toast.error(error.message || 'Bulk action failed');
    } finally {
      setBulkBusy(false);
    }
  };

  if (loading && shipments.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-3xl font-serif">Shipments ({totalShipments})</h1>
        <button
          onClick={loadShipments}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 border rounded-lg border-input hover:bg-muted/50 disabled:opacity-50"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70" size={20} />
          <input
            type="text"
            placeholder="Search by order number or AWB..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value)}
          className="px-3 py-2 text-sm border border-input rounded-lg"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <select
          value={orderTypeFilter}
          onChange={(e) => handleOrderTypeChange(e.target.value)}
          className="px-3 py-2 text-sm border border-input rounded-lg"
        >
          {ORDER_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-4 flex flex-wrap gap-4 text-sm text-muted-foreground">
        <span>
          Total declared value:{' '}
          <span className="font-semibold text-foreground">{formatMoney(totals.declared_value)}</span>
        </span>
        <span>
          Total shipping cost:{' '}
          <span className="font-semibold text-foreground">{formatMoney(totals.shipping_charge)}</span>
        </span>
        <span className="text-muted-foreground/70">
          (real totals across every matching shipment, not just this page)
        </span>
      </div>

      <BulkActionBar count={selection.count} onClear={selection.clear}>
        <BulkActionBar.Button onClick={() => setBulkDialog('status')}>Update status</BulkActionBar.Button>
        <BulkActionBar.Button onClick={() => setBulkDialog('label')} disabled={selection.count > MAX_LABELS}>
          {selection.count > MAX_LABELS ? `Buy labels (max ${MAX_LABELS})` : 'Buy labels'}
        </BulkActionBar.Button>
        <BulkActionBar.Button onClick={() => setBulkDialog('email')}>Resend tracking email</BulkActionBar.Button>
      </BulkActionBar>

      <div className="bg-card border rounded-lg border-border shadow-sm overflow-x-auto">
        <table className="w-full min-w-[960px]">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="pl-6 py-3">
                <HeaderCheckbox selection={selection} />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Order #</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Recipient</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Courier</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">AWB</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                Declared Value
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                Shipping Cost
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Shipped</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {shipments.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-6 py-12 text-center text-muted-foreground">
                  No shipments found
                </td>
              </tr>
            ) : (
              shipments.map((shipment) => (
                <tr key={shipment.id} className="hover:bg-muted/50">
                  <td className="pl-6 py-4">
                    <RowCheckbox
                      selection={selection}
                      id={shipment.id}
                      label={`Select shipment for ${shipment.order_number}`}
                    />
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-foreground">
                    {shipment.order_number}
                    {shipment.order_type && shipment.order_type !== 'standard' && (
                      <span className="ml-2 inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-pink-100 dark:bg-pink-900/40 text-pink-800 dark:text-pink-300">
                        {shipment.order_type}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground">{shipment.recipient_name || 'N/A'}</td>
                  <td className="px-6 py-4 text-sm text-foreground">
                    {shipment.courier_name || shipment.provider || 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground font-mono">{shipment.awb || '—'}</td>
                  <td className="px-6 py-4 text-center">
                    <span
                      className={`inline-flex px-3 py-1 rounded-md text-xs font-medium ${statusColor(shipment.status)}`}
                    >
                      {shipment.status?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-right text-foreground">
                    {shipment.declared_value != null ? formatMoney(shipment.declared_value) : '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-right text-foreground">
                    {shipment.shipping_charge != null ? formatMoney(shipment.shipping_charge) : '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{formatDate(shipment.shipped_at)}</td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      {shipment.tracking_url && (
                        <a
                          href={shipment.tracking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-foreground"
                          title="Open tracking page"
                        >
                          <ExternalLink size={16} />
                        </a>
                      )}
                      <button
                        onClick={() => setSelectedOrderId(shipment.order_id)}
                        className="text-muted-foreground hover:text-foreground"
                        title="View order"
                      >
                        <Eye size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-input rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted/50"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-input rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted/50"
          >
            Next
          </button>
        </div>
      )}

      {bulkDialog === 'status' && (
        <BulkConfirmModal
          title="Update shipment status"
          message="Delivered, cancelled and returned shipments, and ones already at this status, are skipped. This does not email the customer."
          count={selection.count}
          confirmLabel="Update"
          busy={bulkBusy}
          onConfirm={() => runBulkAction('set_status', { status: bulkStatus }, 'updated')}
          onClose={() => setBulkDialog(null)}
        >
          <select
            aria-label="New status"
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          >
            {BULK_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </BulkConfirmModal>
      )}
      {bulkDialog === 'label' && (
        <BulkConfirmModal
          title="Buy shipping labels"
          message="This charges your carrier wallet once per shipment. Shipments that already have a label are skipped."
          count={selection.count}
          confirmLabel="Buy labels"
          danger
          requireCount
          busy={bulkBusy}
          onConfirm={() => runBulkAction('generate_label', {}, 'labelled')}
          onClose={() => setBulkDialog(null)}
        />
      )}
      {bulkDialog === 'email' && (
        <BulkConfirmModal
          title="Resend tracking emails"
          message="Each customer gets their tracking email again. Cancelled shipments are skipped."
          count={selection.count}
          confirmLabel="Send"
          busy={bulkBusy}
          onConfirm={() => runBulkAction('resend_email', {}, 'queued')}
          onClose={() => setBulkDialog(null)}
        />
      )}

      {selectedOrderId && (
        <OrderDetailModal
          orderId={selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
          onUpdate={() => loadShipments()}
        />
      )}
    </div>
  );
};

export default ShipmentsPage;
